import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload, FileSpreadsheet, Check, AlertTriangle, Landmark, ArrowDownLeft,
  ArrowUpRight, X, Loader2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { vouchersApi, customersApi, BankStatementRow, BankImportPreviewRow } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/**
 * Column headings vary between banks and even between exports from the same
 * bank, so headers are matched loosely rather than by exact string.
 */
const COLUMN_MATCHERS: Record<keyof BankStatementRow | 'ignore', RegExp> = {
  date: /^date$/i,
  valueDate: /value\s*dt|value\s*date/i,
  narration: /narration|description|particular|remark/i,
  refNo: /chq|ref/i,
  withdrawal: /withdraw|debit|dr\b/i,
  deposit: /deposit|credit|cr\b/i,
  ignore: /^$/,
};

/** Strips currency symbols, thousands separators and stray spaces. */
function toAmount(v: any): number {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return isFinite(n) ? n : 0;
}

/**
 * Excel stores dates as a serial number counted from 1900. A cell formatted as
 * a date therefore arrives as e.g. 46266 rather than "11/09/26", so it has to
 * be converted back before the server can read it.
 */
function cellToDateString(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) {
      const yy = String(d.y).padStart(4, '0');
      return `${String(d.d).padStart(2, '0')}/${String(d.m).padStart(2, '0')}/${yy}`;
    }
  }
  return String(v).trim();
}

/**
 * Defined at module scope, NOT inside BankImport.
 *
 * A component declared inside another component is a new function identity on
 * every render, so React treats it as a different type, unmounts the old
 * subtree and mounts a fresh one. The input node is destroyed and recreated on
 * each keystroke, which drops focus — you type one character and the field goes
 * dead until you click it again.
 */
const LedgerPicker: React.FC<{
  label: string;
  hint: string;
  value: { id: number; company: string } | null;
  query: string;
  setQuery: (s: string) => void;
  results: any[];
  onSearch: (q: string) => void;
  onPick: (l: any) => void;
  onClear: () => void;
}> = ({ label, hint, value, query, setQuery, results, onSearch, onPick, onClear }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {value ? (
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
        <span className="text-sm text-blue-900 truncate">{value.company}</span>
        <button onClick={onClear} className="text-xs text-blue-600 shrink-0">Change</button>
      </div>
    ) : (
      <>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); onSearch(e.target.value); }}
          placeholder={hint}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
        />
        {results.length > 0 && (
          <div className="mt-1 border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-44 overflow-y-auto">
            {results.map(r => (
              <button key={r.id} onClick={() => onPick(r)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                {r.company}
              </button>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

const BankImport: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState<BankStatementRow[]>([]);
  const [preview, setPreview] = useState<BankImportPreviewRow[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  const [bankQuery, setBankQuery] = useState('');
  const [bankResults, setBankResults] = useState<any[]>([]);
  const [bank, setBank] = useState<{ id: number; company: string } | null>(null);
  const [suspense, setSuspense] = useState<{ id: number; company: string } | null>(null);
  const [suspenseQuery, setSuspenseQuery] = useState('');
  const [suspenseResults, setSuspenseResults] = useState<any[]>([]);

  const searchLedger = useCallback(async (q: string, set: (r: any[]) => void) => {
    if (q.trim().length < 2) { set([]); return; }
    try {
      const res = await customersApi.searchAllLedgers(q.trim());
      set(res.data || []);
    } catch {
      set([]);
    }
  }, []);

  /** Parse the workbook in the browser — the API never sees the file. */
  const onFile = async (file: File) => {
    setParsing(true);
    setPreview([]);
    setSummary(null);
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const grid: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' });

      // Bank exports carry a title block above the table, so the header row has
      // to be found rather than assumed to be row 1.
      let headerIdx = -1;
      let map: Record<string, number> = {};
      for (let i = 0; i < Math.min(grid.length, 30); i++) {
        const cells = grid[i].map(c => String(c || '').trim());
        const candidate: Record<string, number> = {};
        cells.forEach((cell, idx) => {
          for (const [field, re] of Object.entries(COLUMN_MATCHERS)) {
            if (field === 'ignore') continue;
            if (candidate[field] === undefined && re.test(cell)) candidate[field] = idx;
          }
        });
        if (candidate.narration !== undefined &&
            (candidate.withdrawal !== undefined || candidate.deposit !== undefined)) {
          headerIdx = i;
          map = candidate;
          break;
        }
      }

      if (headerIdx === -1) {
        showError('Could not read this file',
          'No row with Narration plus Withdrawal/Deposit columns was found. Is this a bank statement export?');
        return;
      }

      const parsed: BankStatementRow[] = [];
      for (let i = headerIdx + 1; i < grid.length; i++) {
        const r = grid[i];
        if (!r || r.every(c => String(c || '').trim() === '')) continue;

        const narration = String(r[map.narration] ?? '').trim();
        const withdrawal = map.withdrawal !== undefined ? toAmount(r[map.withdrawal]) : 0;
        const deposit = map.deposit !== undefined ? toAmount(r[map.deposit]) : 0;

        // Separator rows of asterisks and closing-balance footers carry no
        // amounts; skipping them here keeps the preview free of noise rows the
        // user would only have to read past.
        if (!narration || /^\*+$/.test(narration)) continue;
        if (withdrawal === 0 && deposit === 0) continue;

        parsed.push({
          date: cellToDateString(r[map.date]),
          valueDate: cellToDateString(r[map.valueDate] ?? r[map.date]),
          narration,
          refNo: map.refNo !== undefined ? String(r[map.refNo] ?? '').trim() : '',
          withdrawal,
          deposit,
        });
      }

      if (parsed.length === 0) {
        showError('Nothing to import', 'The file was read but held no transaction rows.');
        return;
      }
      setRows(parsed);
      showSuccess('File read', `${parsed.length} transaction rows found.`);
    } catch (e: any) {
      showError('Could not read this file', e?.message || 'Unsupported or corrupt file');
    } finally {
      setParsing(false);
    }
  };

  const runPreview = async () => {
    if (!bank) { showError('Bank ledger required', 'Choose which bank account this statement belongs to.'); return; }
    setImporting(true);
    try {
      const res = await vouchersApi.bankImportPreview(rows, bank.id);
      setPreview(res.data || []);
      setSummary(res.summary);
    } catch (e: any) {
      showError('Preview failed', e?.message || 'Unknown error');
    } finally {
      setImporting(false);
    }
  };

  const runImport = async () => {
    if (!bank || !suspense) return;
    setImporting(true);
    try {
      const res = await vouchersApi.bankImportCommit(rows, bank.id, suspense.id);
      showSuccess(
        'Import complete',
        `${res.imported} posted, ${res.skipped} already imported, ${res.failed} failed.`
      );
      if (res.errors?.length) console.warn('[BankImport] row errors:', res.errors);
      // Re-run the preview so every row now reads as a duplicate — proof the
      // import landed, and it makes an accidental second press a no-op.
      await runPreview();
    } catch (e: any) {
      showError('Import failed', e?.message || 'Unknown error');
    } finally {
      setImporting(false);
    }
  };

  const importable = useMemo(
    () => preview.filter(r => !r.duplicate && !r.error).length,
    [preview]
  );

  return (
    <div className="p-4 max-w-5xl mx-auto pb-24">
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900">Bank statement import</h1>
        <p className="text-sm text-gray-500">
          Deposits become Receipts and withdrawals become Payments, both against the
          suspense ledger. The narration is carried into the voucher remark and the
          value date is used as the voucher date.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <LedgerPicker
          label="Bank ledger" hint="Search the bank account, e.g. HDFC"
          value={bank} query={bankQuery} setQuery={setBankQuery} results={bankResults}
          onSearch={q => searchLedger(q, setBankResults)}
          onPick={l => { setBank(l); setBankResults([]); setPreview([]); setSummary(null); }}
          onClear={() => { setBank(null); setBankQuery(''); setPreview([]); setSummary(null); }}
        />
        <LedgerPicker
          label="Suspense ledger" hint="Search the suspense ledger"
          value={suspense} query={suspenseQuery} setQuery={setSuspenseQuery} results={suspenseResults}
          onSearch={q => searchLedger(q, setSuspenseResults)}
          onPick={l => { setSuspense(l); setSuspenseResults([]); }}
          onClear={() => { setSuspense(null); setSuspenseQuery(''); }}
        />
      </div>

      <input
        ref={fileRef} type="file" accept=".xls,.xlsx,.csv" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={parsing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-50"
        >
          {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {fileName || 'Choose statement file'}
        </button>

        {rows.length > 0 && (
          <button
            onClick={runPreview}
            disabled={!bank || importing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium disabled:opacity-50"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Check {rows.length} rows
          </button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          {[
            { label: 'Rows', value: summary.total, tone: 'text-gray-900' },
            { label: 'Receipts', value: summary.receipts, tone: 'text-emerald-600' },
            { label: 'Payments', value: summary.payments, tone: 'text-blue-600' },
            { label: 'Already in', value: summary.duplicates, tone: 'text-amber-600' },
            { label: 'Problems', value: summary.errors, tone: 'text-red-600' },
          ].map(s => (
            <div key={s.label} className="text-center py-2 rounded-lg bg-gray-50">
              <div className={`text-base font-semibold ${s.tone}`}>{s.value}</div>
              <div className="text-[11px] text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {preview.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Narration</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-left px-3 py-2 font-medium">Posts as</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={`${r.key}-${i}`}
                      className={`border-t border-gray-50 ${
                        r.error ? 'bg-red-50' : r.duplicate ? 'bg-amber-50' : ''
                      }`}>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.valueDate}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-md truncate" title={r.narration}>
                      {r.narration}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                      {r.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 ${
                        r.kind === 'receipt' ? 'text-emerald-700' : 'text-blue-700'
                      }`}>
                        {r.kind === 'receipt'
                          ? <ArrowDownLeft className="h-3 w-3" />
                          : <ArrowUpRight className="h-3 w-3" />}
                        {r.kind === 'receipt' ? 'Receipt' : 'Payment'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.error ? (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <AlertTriangle className="h-3 w-3" /> {r.error}
                        </span>
                      ) : r.duplicate ? (
                        <span className="text-amber-700">Already imported</span>
                      ) : (
                        <span className="text-gray-400">New</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 p-3 border-t border-gray-100 bg-white">
            <span className="text-xs text-gray-500">
              {importable} of {preview.length} rows will be posted
              {summary?.duplicates > 0 && ` · ${summary.duplicates} skipped as already imported`}
            </span>
            <button
              onClick={runImport}
              disabled={importing || importable === 0 || !bank || !suspense}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Import {importable}
            </button>
          </div>

          {!suspense && importable > 0 && (
            <div className="px-3 pb-3 text-xs text-amber-600">
              Choose the suspense ledger before importing.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BankImport;
